import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FieldError, Portal, svgSprite } from '../../hybrid/wrappers';
import Skeleton from '../plug/Skeleton/Skeleton';
import css from './AutoUpgrade.module.scss';
import { createGridData, gridThead, handlers, skeleton, transformSendData } from './helpers';
import UserContext, { UserProfileContext } from '../../common/utils/getContext';
import { dataSender, getData } from '../../common/utils/router';
import GridComponent from '../grid/GridComponent/GridComponent';
import classNames from 'classnames';
import T, { t } from '../../common/components/T';
import SingleButton from '../form/form-control-buttons/SingleButton/SingleButton';
import { API_SERVER_URL } from '../../common/utils/utils';
import FormDropdown from '../form/components/form-dropdown/FormDropdown/FormDropdown';
import { LangContext } from '../../common/decorators/Language';
import Table from './components/Table/Table';
import { useWindowDimensions } from '../../common/utils/windowDimensions';

const RenderData = (props) => {
    const { gridData, setGridData, closeHandler, initialData, available } = props;
    const { userToken, lang, setAlertData } = useContext(UserContext);
    const { profileData } = useContext(UserProfileContext);
    const { translation } = useContext(LangContext);
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [initValue, setInitValue] = useState(initialData);
    const [errors, setErrors] = useState([]);

    const mobile = useWindowDimensions() < 768;

    const sumOfProducts = useMemo(() => {
        let sum = 0;
        let currency = '';
        initValue?.products?.forEach((product) => {
            const count = product.count;
            currency = product.product?.price?.currency_symbol;
            sum += product.product?.price?.price_for_client * count;
        });
        return `${sum} ${currency}`;
    }, [initValue]);

    const priceRange = useMemo(() => {
        const fromPrice = Math.min(...(available?.map((el) => el?.price) || []));
        const toPrice = Math.max(...(available?.map((el) => el?.price) || []));

        if (fromPrice === toPrice) {
            return {
                price: fromPrice,
                debitedPrice: available[0]?.account_price
            };
        }
        return {
            from: fromPrice,
            to: toPrice
        };
    }, [available]);

    const enoughUpgrade = useMemo(() => {
        const sum = sumOfProducts?.split(' ')[0];
        if (priceRange?.price) {
            return +sum === +priceRange.price;
        } else {
            return +sum >= +priceRange?.from;
        }
    }, [sumOfProducts, priceRange]);

    // фільтр для складів відносно місцезнаходження юзера
    const warehousesForUser = useMemo(() => {
        let filters = [];
        const array = ['country_id', 'region_id', 'city_id'];
        const { country, region, city } = profileData;
        [country, region, city]
            .filter((el) => el?.geoname_id)
            .forEach((it, idx) => {
                filters.push(`filter[${array[idx]}]=${it.geoname_id}`);
            });
        return filters.join('&');
    }, [profileData]);

    const sender = useCallback(() => {
        setBtnDisabled(true);
        const normalizedData = transformSendData(initValue);
        dataSender(
            `${API_SERVER_URL}/api/v1/user/autoupgrade`,
            'POST',
            normalizedData,
            () => {
                closeHandler();
            },
            (error) => {
                if (error.errors) {
                    setErrors(error.errors);
                }
                setAlertData({
                    type: 'error',
                    cont: error?.message || <T textName='error_occured' defaultStr='Произошла ошибка' />
                });
                setBtnDisabled(false);
            },
            userToken,
            lang
        );
    }, [userToken, lang, initValue, setAlertData, closeHandler]);

    return (
        <div className={css.modal}>
            <div onClick={closeHandler} className={classNames('circleButton', css.modalClose)}>
                {svgSprite('cross', { className: css.modalCloseSvg })}
            </div>
            <p className={css.title}>
                <T textName='activate-package-autoupgrade' defaultStr='Активировать авто-апгрейд пакета' />
            </p>
            <p className={css.undertitle}>
                <T
                    textName='package-autoupgrade-undertitle'
                    defaultStr='Выберите товары, которые автоматически будут приобретены для повышения пакета'
                />
            </p>
            <div className={css.infoBlock}>
                <div
                    className={classNames(
                        css.infoSum,
                        { [css.infoSumGreen]: enoughUpgrade },
                        { [css.infoSumRed]: !enoughUpgrade }
                    )}
                >
                    <p className={css.infoSumTitle}>
                        <T textName='sum-of-selected-products' defaultStr='Выбрано товаров на сумму' />
                    </p>
                    <p className={css.infoSumValue}>
                        <span className={css.infoSumValueNum}>{sumOfProducts?.split(' ')[0]}</span>
                        <span> </span>
                        <span className={css.infoSumValueCurr}>{sumOfProducts?.split(' ')[1]}</span>
                    </p>
                </div>
                <div className={css.infoBlockRight}>
                    <div className={css.infoBlockRightItem}>
                        <span className={css.label}>
                            <T textName='amount-to-increase' defaultStr='Сумма для повышения' />:
                        </span>
                        <span className={css.value}>
                            {priceRange?.price
                                ? `${priceRange?.price} PV`
                                : `${t('from-autoupgrade', 'от', 'all', translation)} ${priceRange?.from} ${t(
                                      'to-autoupgrade',
                                      'до',
                                      'all',
                                      translation
                                  )} ${priceRange?.to} PV`}
                        </span>
                    </div>
                    {priceRange?.debitedPrice ? (
                        <div className={css.infoBlockRightItem}>
                            <span className={css.label}>
                                <T textName='debited_from_wallet' defaultStr='Спишется с кошелька' />:
                            </span>
                            <span className={css.value}>{priceRange?.debitedPrice + ' PV'}</span>
                        </div>
                    ) : null}
                    <div className={css.infoBlockRightItem}>
                        <span className={css.label}>
                            <T textName='for-increase' defaultStr='Для повышения' />:
                        </span>
                        <span
                            className={classNames(
                                css.value,
                                { [css.valueEnough]: enoughUpgrade },
                                { [css.valueNotEnough]: !enoughUpgrade }
                            )}
                        >
                            {enoughUpgrade ? (
                                <T textName='enough' defaultStr='Достаточно' />
                            ) : (
                                <T textName='not-enough' defaultStr='Не достаточно' />
                            )}
                        </span>
                    </div>
                </div>
            </div>
            <FormDropdown
                attribute='warehouse'
                getOptions={{
                    url: `/api/v1/warehouse/warehouses/for-shop?${warehousesForUser}`,
                    searchUrl: `/api/v1/warehouse/warehouses/for-shop?${warehousesForUser}`,
                    sorter: (data) => data?.map((item) => ({ ...item, label: item?.name || '-', value: item?.id }))
                }}
                title={{
                    body: <T textName='choose-warehouse' defaultStr='Выберите склад' />
                }}
                setFormData={(attr, val) => {
                    setInitValue((s) => ({
                        ...s,
                        [attr]: val
                    }));
                }}
                errors={errors['warehouse_id']}
            />
            <GridComponent
                viewData={gridData.tbody}
                viewThead={gridData.thead}
                convertData={createGridData}
                handlers={handlers(setInitValue, setGridData)}
                pageSettings={{
                    view: mobile ? Table : null,
                    params: {
                        handlers: handlers(setInitValue, setGridData)
                    }
                }}
                gridStyles={{
                    tableWrapper: {
                        wrapper: css.tableWrapper
                    },
                    activeRow: {
                        wrapper: css.activeRowWrapper
                    }
                }}
            />
            {errors ? (
                <div className={css.errors}>
                    {Object.keys(errors).map((error) =>
                        error !== 'warehouse_id' ? <FieldError key={error}>{errors[error]}</FieldError> : null
                    )}
                </div>
            ) : null}
            <div className={css.applyBtn}>
                <SingleButton
                    type='ok'
                    buttonText={<T textName='confirm' defaultStr='Подтвердить' />}
                    customButton={{
                        butWrapper: css.applyBtnWrapper
                    }}
                    disabled={btnDisabled || !enoughUpgrade}
                    clickFunc={sender}
                />
            </div>
        </div>
    );
};

export default function AutoUpgrade(props) {
    const { closeHandler, initialData } = props;
    const { userToken, lang } = useContext(UserContext);
    const [data, setData] = useState(null);
    const [gridData, setGridData] = useState(null);

    useEffect(() => {
        getData(
            '/api/v1/shop/products?page[size]=999&filter[product_availability]=in_stock,expected_arrival',
            userToken,
            lang
        ).then(setData);
    }, [userToken, lang]);

    useEffect(() => {
        if (data) {
            setGridData({ thead: gridThead, tbody: createGridData(data, initialData) });
        }
    }, [data, initialData]);

    return (
        <Portal closeHandler={closeHandler}>
            <Skeleton
                wait={!!gridData}
                Component={RenderData}
                pattern={skeleton}
                componentProps={{ ...props, gridData, setGridData }}
                wrapperStyles={css.skeleton}
            />
        </Portal>
    );
}
